import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

const extractError = (error) =>
    error.response?.data?.message || error.message || 'Request failed';

// ---- Admin: global config ----
export const getAdminPaymentConfig = createAsyncThunk(
    'payment/getAdminPaymentConfig',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/payment/admin/config`);
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

export const updateAdminPaymentConfig = createAsyncThunk(
    'payment/updateAdminPaymentConfig',
    async ({ methods, timingPolicy }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/payment/admin/config`, { methods, timingPolicy });
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// ---- Platform maintenance (super_admin) ----
// A hold is a temporary cover over a payment method during an outage. It never
// touches a hospital's own config, so releasing it restores every hospital to
// what it had — nobody has to switch anything back on.

export const getPaymentHolds = createAsyncThunk(
    'payment/getPaymentHolds',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/payment/platform/holds`);
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// tenantId omitted/null => every hospital. Set => that one hospital only.
export const setPaymentHold = createAsyncThunk(
    'payment/setPaymentHold',
    async ({ method, tenantId = null, held, reason }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/payment/platform/holds`, {
                method, tenantId, held, reason,
            });
            // Re-read rather than patch locally: affected-hospital counts and the
            // audit history are computed server-side.
            await dispatch(getPaymentHolds());
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// ---- Doctor: self-serve settings + bank ----
export const getDoctorPaymentSettings = createAsyncThunk(
    'payment/getDoctorPaymentSettings',
    async (doctorId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/payment/doctor/${doctorId}/settings`);
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

export const updateDoctorPaymentSettings = createAsyncThunk(
    'payment/updateDoctorPaymentSettings',
    async ({ doctorId, acceptedPaymentMethods, paymentTimingPreference }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/payment/doctor/${doctorId}/settings`, {
                acceptedPaymentMethods,
                paymentTimingPreference,
            });
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

export const updateDoctorBankAccount = createAsyncThunk(
    'payment/updateDoctorBankAccount',
    async ({ doctorId, bankAccounts }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/payment/doctor/${doctorId}/bank-account`, { bankAccounts });
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// ---- Booking: resolved options ----
export const getPaymentOptions = createAsyncThunk(
    'payment/getPaymentOptions',
    async (doctorId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/payment/options`, { params: { doctorId } });
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// ---- Collection ----
export const markPaymentCollected = createAsyncThunk(
    'payment/markPaymentCollected',
    async (appointmentId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.patch(`${baseURL}/payment/${appointmentId}/collect`);
            return { appointmentId, ...res.data };
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// Toggle Paid <-> Not Paid (bidirectional). paid:boolean.
export const setPaymentStatus = createAsyncThunk(
    'payment/setPaymentStatus',
    async ({ appointmentId, paid }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.patch(`${baseURL}/payment/${appointmentId}/status`, { paid });
            return { appointmentId, ...res.data };
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// Change the recorded payment method (backend enforces prepaid != cash).
export const changePaymentMethod = createAsyncThunk(
    'payment/changePaymentMethod',
    async ({ appointmentId, method }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.patch(`${baseURL}/payment/${appointmentId}/method`, { method });
            return { appointmentId, ...res.data };
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// Patient (or staff) settles an appointment online/card before completion.
// Marks it Paid and records the method. Cash is never self-paid (in-person only).
export const payAppointment = createAsyncThunk(
    'payment/payAppointment',
    async ({ appointmentId, method }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.patch(`${baseURL}/payment/${appointmentId}/pay`, { method });
            return { appointmentId, ...res.data };
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

const paymentSlice = createSlice({
    name: 'payment',
    initialState: {
        adminConfig: { methods: [], timingPolicy: 'postpaid' },
        // Super Admin's maintenance view of payment-method holds.
        holds: null,
        holdsLoading: false,
        doctorSettings: null,
        paymentOptions: { methods: [], timing: 'postpaid', gatewayReady: false },
        // Whether /payment/options actually ANSWERED. An empty `methods` list is a
        // real answer — the hospital disabled the method, the doctor accepts none,
        // Super Admin put one on hold, or the plan lacks it (the endpoint returns
        // the intersection). So a booking screen may only fall back to a hard-coded
        // method list when the REQUEST FAILED, never when it succeeded with nothing.
        // 'idle' | 'loading' | 'succeeded' | 'failed'.
        paymentOptionsStatus: 'idle',
        loading: false,
        error: null,
        // Per-appointment mutation tracking so each row/button shows its own spinner
        // instead of a page-wide loader. Keyed by appointmentId -> boolean.
        mutating: {},
        mutationError: null,
    },
    reducers: {
        clearPaymentOptions: (state) => {
            state.paymentOptions = { methods: [], timing: 'postpaid', gatewayReady: false };
            state.paymentOptionsStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAdminPaymentConfig.fulfilled, (state, action) => { state.adminConfig = action.payload; })
            .addCase(getPaymentHolds.pending, (state) => { state.holdsLoading = true; })
            .addCase(getPaymentHolds.fulfilled, (state, action) => {
                state.holdsLoading = false;
                state.holds = action.payload || null;
            })
            .addCase(getPaymentHolds.rejected, (state) => {
                state.holdsLoading = false;
                state.holds = null;
            })
            .addCase(updateAdminPaymentConfig.fulfilled, (state, action) => { state.adminConfig = action.payload; })
            .addCase(getDoctorPaymentSettings.fulfilled, (state, action) => { state.doctorSettings = action.payload; })
            // Keep doctorSettings fresh after self-serve saves so the profile
            // tab-completion lock reacts without a re-fetch.
            .addCase(updateDoctorPaymentSettings.fulfilled, (state, action) => {
                if (state.doctorSettings) state.doctorSettings = { ...state.doctorSettings, ...action.payload };
            })
            .addCase(updateDoctorBankAccount.fulfilled, (state, action) => {
                if (state.doctorSettings) state.doctorSettings = { ...state.doctorSettings, bankAccounts: action.payload.bankAccounts, bankAccount: action.payload.bankAccount };
            })
            .addCase(getPaymentOptions.pending, (state) => { state.loading = true; state.paymentOptionsStatus = 'loading'; })
            .addCase(getPaymentOptions.fulfilled, (state, action) => {
                state.loading = false;
                state.paymentOptionsStatus = 'succeeded';
                state.paymentOptions = action.payload;
            })
            .addCase(getPaymentOptions.rejected, (state, action) => {
                state.loading = false;
                // Only a FAILED request licenses a client-side fallback list.
                state.paymentOptionsStatus = 'failed';
                state.error = action.payload;
            });

        // Per-appointment payment mutations (status toggle + method change) share the
        // same per-id pending/fulfilled/rejected bookkeeping.
        [setPaymentStatus, changePaymentMethod, payAppointment].forEach((thunk) => {
            builder
                .addCase(thunk.pending, (state, action) => {
                    state.mutationError = null;
                    state.mutating[action.meta.arg.appointmentId] = true;
                })
                .addCase(thunk.fulfilled, (state, action) => {
                    state.mutating[action.payload.appointmentId] = false;
                })
                .addCase(thunk.rejected, (state, action) => {
                    state.mutating[action.meta.arg.appointmentId] = false;
                    state.mutationError = action.payload;
                });
        });
    },
});

export const { clearPaymentOptions } = paymentSlice.actions;
export default paymentSlice.reducer;
